import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { metricKeys } from '@pulse/metrics';
import {
  MetricTitle,
  MetricWidgetWrapper,
  MetricContent,
  MetricChart,
} from '@components/overview-dashboard/styled';
import Chart from '@components/overview-dashboard/common/chart/Chart';
import updateYAxisMaxPercentage from '@components/overview-dashboard/common/chart/utils';
import { initMetric } from '@store/overviewDashboard/metricsData/actions';
import { initBenchmarkSPI } from '@store/overviewDashboard/benchmark/actions';
import { metricDataSelector } from '@store/overviewDashboard/metricsData/selectors';
import { getMetricHelpText, getMetricOvbDisplayName } from '@src/utils/metrics';
import { fetchWidgetMessages } from '@store/overviewDashboard/widgetMessageData/actions';
import { HelpPopup, useHelpPopup } from '@components/overview-dashboard/common/help-popup';
import { IconWrapper } from '@components/overview-dashboard/common/help-popup/styled';
import { StyledPara } from '@components/error-logic-config/ErrorLogicTable/styled';
import {
  ovbDeviceMetricsEnabledSelector,
  ovbViewersMetricsEnabledSelector,
} from '@store/common/selectors';
import { Retry } from '@components/common';
import { selectedKPIConfigSelector } from '@store/overviewDashboard/base/selectors';
import SettingsIco from '@icons/ovb/settings.svg';
import { formatData } from './utils';
import InfoTab from './InfoTab';
import SPIMetricsText from './SPIMetricsText';
import SPIPopup from './spi-threshold-popup';
import { TitleContainer } from '../metrics-widget/styled';

const WidgetChart = ({ chartOption }) => {
  const height = '100px';
  const width = '100%';
  updateYAxisMaxPercentage({ options: chartOption, unit: '%' });
  return chartOption ? (
    <MetricChart>
      <Chart option={chartOption} height={height} width={width} />
    </MetricChart>
  ) : null;
};

const SPIWidget = ({ currentShareId, pageType }) => {
  const dispatch = useDispatch();
  const selectedMetric = metricKeys.StreamingPerformanceIndex;
  const metricData = useSelector(metricDataSelector);
  const [isSPIPopupOpen, setSPIPopup] = useState(false);
  const selectedKPIConfig = useSelector(selectedKPIConfigSelector);
  const kpiName = `SPI: ${selectedKPIConfig.kpiName}`;

  const showDevicesHelp = useSelector(ovbDeviceMetricsEnabledSelector);
  const showViewersHelp = useSelector(ovbViewersMetricsEnabledSelector);
  const { helpContext, HelpIcon } = useHelpPopup();

  useEffect(() => {
    dispatch(initMetric({ metric: selectedMetric }));
    dispatch(initBenchmarkSPI());
  }, [selectedMetric, dispatch]);

  const openSPIPopup = () => {
    setSPIPopup(true);
  };
  const cancelEdit = () => setSPIPopup(!isSPIPopupOpen);

  const renderWidgetTitle = () => (
    <TitleContainer>
      <MetricTitle style={{ flex: 1 }}>{kpiName}</MetricTitle>
      <IconWrapper onClick={openSPIPopup}>
        <SettingsIco />
      </IconWrapper>
      <HelpIcon />
    </TitleContainer>
  );

  const renderMetricNumbersAndText = () => (
    <SPIMetricsText metricData={metricData} selectedMetric={selectedMetric} />
  );

  const renderPopup = () => (
    <SPIPopup
      isSPIPopupOpen={isSPIPopupOpen}
      cancelEdit={cancelEdit}
      currentShareId={currentShareId}
      pageType={pageType}
    />
  );

  const { chartData, isLoading, isError, isEmpty } = useMemo(
    () => formatData(metricData[selectedMetric]),
    [metricData[selectedMetric]],
  );

  const renderHelpPopup = () => {
    const metricHelpText = getMetricHelpText({ metricKey: selectedMetric });
    const data: any = [<StyledPara key="spi">{metricHelpText}</StyledPara>];
    if (showDevicesHelp) {
      const metricHelpText = getMetricHelpText({ metricKey: metricKeys.BadUniqueDevices });
      data.push([<StyledPara key="devices">{metricHelpText}</StyledPara>]);
    }
    if (showViewersHelp) {
      const metricHelpText = getMetricHelpText({ metricKey: metricKeys.BadUniqueViewers });
      data.push([<StyledPara key="viewers">{metricHelpText}</StyledPara>]);
    }
    const ovbDisplayName = getMetricOvbDisplayName({ metricKey: selectedMetric });
    return <HelpPopup title={ovbDisplayName} helpText={data} helpContext={helpContext} />;
  };

  const renderContent = () => (
    <MetricContent>
      <Retry
        isLoading={isLoading}
        showError={isError}
        fetchData={fetchDataOnRetry}
        isEmpty={isEmpty}
      >
        <React.Fragment key="spi-chart">
          {renderMetricNumbersAndText()}
          <WidgetChart chartOption={chartData} />
          {renderPopup()}
          {renderHelpPopup()}
        </React.Fragment>
      </Retry>
    </MetricContent>
  );

  const fetchDataOnRetry = useCallback(() => {
    dispatch(initMetric({ metric: selectedMetric }));
    dispatch(fetchWidgetMessages());
  }, [selectedMetric]);

  return (
    <MetricWidgetWrapper id="spi-widget">
      {renderWidgetTitle()}
      {renderContent()}
      <InfoTab />
    </MetricWidgetWrapper>
  );
};

export default SPIWidget;
